import { sanitizeAddress } from './sanitizeAddress';

/**
 * On-device "intent history". Stores the last few searches so a returning
 * parent can resume without re-typing. The full address is kept (localStorage,
 * this device only) so a resume re-runs the identical search; the `label` is
 * sanitized for display and is what the UI renders.
 */
export type RecentSearchConfidence = 'high' | 'low' | 'failed';

export interface RecentSearch {
  id: string;
  address: string; // full address — re-runs the search; never displayed raw
  label: string; // house-number-stripped display string ('' → pinned location)
  grade: string;
  lat: number | null;
  lng: number | null;
  confidence: RecentSearchConfidence;
  // Parsed from the geocode result; carried through so a resume re-emits the
  // same city/state/zip analytics. zip stays a string (leading zeros).
  city?: string;
  state?: string;
  zip?: string;
  timestamp: number; // ms epoch
}

export interface RecentSearchInput {
  address: string;
  grade: string;
  lat?: number | null;
  lng?: number | null;
  confidence?: RecentSearchConfidence;
  city?: string;
  state?: string;
  zip?: string;
}

const STORAGE_KEY = 'finderRecentSearches';
export const RECENT_SEARCHES_CHANGE_EVENT = 'finderRecentSearches:changed';
const MAX_RECENT = 3;

function emitChange(): void {
  try {
    window.dispatchEvent(new Event(RECENT_SEARCHES_CHANGE_EVENT));
  } catch {
    // no-op outside the browser
  }
}

function makeId(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    // fall through to the timestamp-based id
  }
  return `r_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object';
}

function reviveOne(raw: unknown): RecentSearch | null {
  if (!isRecord(raw)) return null;
  if (typeof raw.address !== 'string' || typeof raw.grade !== 'string') return null;
  const lat = raw.lat == null ? null : Number(raw.lat);
  const lng = raw.lng == null ? null : Number(raw.lng);
  const conf = raw.confidence;
  const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);
  return {
    id: typeof raw.id === 'string' ? raw.id : makeId(),
    address: raw.address,
    label: typeof raw.label === 'string' ? raw.label : sanitizeAddress(raw.address),
    grade: raw.grade,
    lat: Number.isFinite(lat) ? lat : null,
    lng: Number.isFinite(lng) ? lng : null,
    confidence: conf === 'low' || conf === 'failed' ? conf : 'high',
    city: str(raw.city),
    state: str(raw.state),
    zip: str(raw.zip),
    timestamp: typeof raw.timestamp === 'number' ? raw.timestamp : Date.now(),
  };
}

/** Dedupe identity: same grade at the same address (case-insensitive). */
function dedupeKey(s: { grade: string; address: string }): string {
  return `${s.grade}|${s.address.trim().toLowerCase()}`;
}

export function listRecentSearches(): RecentSearch[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(reviveOne)
      .filter((s): s is RecentSearch => s !== null)
      .sort((a, b) => b.timestamp - a.timestamp);
  } catch {
    return [];
  }
}

function write(rows: RecentSearch[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
    emitChange();
  } catch {
    // storage disabled or over quota — recents are best-effort
  }
}

/** Record (or refresh) a search, newest first, capped at MAX_RECENT. */
export function upsertRecentSearch(input: RecentSearchInput): void {
  if (!input.address.trim() || !input.grade.trim()) return;
  const key = dedupeKey(input);
  const existing = listRecentSearches().find((r) => dedupeKey(r) === key);
  const entry: RecentSearch = {
    id: existing?.id ?? makeId(),
    address: input.address,
    label: sanitizeAddress(input.address),
    grade: input.grade,
    lat: input.lat ?? null,
    lng: input.lng ?? null,
    confidence: input.confidence ?? 'high',
    city: input.city,
    state: input.state,
    zip: input.zip,
    timestamp: Date.now(),
  };
  const rest = listRecentSearches().filter((r) => dedupeKey(r) !== key);
  write([entry, ...rest].slice(0, MAX_RECENT));
}

export function removeRecentSearch(id: string): void {
  write(listRecentSearches().filter((r) => r.id !== id));
}

export function clearRecentSearches(): void {
  write([]);
}

/** Build the `/results` query string that re-runs a recent search. */
export function recentSearchToParams(s: RecentSearch): string {
  const params = new URLSearchParams();
  params.set('address', s.address);
  params.set('grade', s.grade);
  if (s.lat != null && s.lng != null) {
    params.set('lat', String(s.lat));
    params.set('lng', String(s.lng));
  }
  if (s.confidence !== 'high') params.set('conf', s.confidence);
  if (s.city) params.set('city', s.city);
  if (s.state) params.set('state', s.state);
  if (s.zip) params.set('zip', s.zip);
  return params.toString();
}
